// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { DisplayUnit, toDisplayValue, fromDisplayValue } from './cadUnits';

/**
 * The user's own vertical nudge (metres) for a geodatabase — what the sidebar's
 * "Elevation offset" field holds, always in metres regardless of its display-unit toggle.
 *
 * This is a DELTA on top of the order's automatic geoid correction, not the total shift, so 0
 * means "sit on the geoid plane" — the geodetically correct position — rather than "no correction
 * at all". Storing the delta also means a geoid correction backfilled onto the order later still
 * takes effect, instead of being overridden by a stale total.
 * @returns the user's nudge in metres; 0 when they have never set one.
 */
export const resolveUserElevationOffset = (storedOffsetZ: number | null | undefined): number => {
  if (typeof storedOffsetZ === 'number' && Number.isFinite(storedOffsetZ)) return storedOffsetZ;
  return 0;
};

/**
 * The automatic geoid correction (metres) for the SELECTED order.
 *
 * Deliberately matched by id: `state.order.list` is not project-filtered, so picking "the first
 * order that happens to carry a value" could apply a neighbouring order's correction — or none at
 * all while a sibling has one. Both were happening before this was matched properly.
 * @returns the selected order's correction, or 0 when it has none.
 */
export const resolveOrderGeoidOffset = (
  orders: { _id: string; geoidOffsetZ?: number }[] | undefined | null,
  selectedOrderId: string | null | undefined,
): number => {
  if (!Array.isArray(orders) || !selectedOrderId) return 0;
  const order = orders.find((candidate) => candidate._id === selectedOrderId);
  const offset = order?.geoidOffsetZ;
  return typeof offset === 'number' && Number.isFinite(offset) ? offset : 0;
};

/**
 * The correction already stored on a project's draft (unplaced) order, for display only. Nothing
 * is applied until the order is placed, so this never feeds the ground offset.
 * @returns the draft's stored correction in metres, or null when it has none.
 */
export const resolvePendingGeoidOffset = (
  draftOrder: { geoidOffsetZ?: number | null } | undefined | null,
): number | null => {
  const offset = draftOrder?.geoidOffsetZ;
  return typeof offset === 'number' && Number.isFinite(offset) ? offset : null;
};

/**
 * The total vertical shift in force (metres) — the automatic geoid correction plus the user's nudge.
 * This is what the sidebar reports as "Applied", so a 0 in the input cannot be read as "nothing
 * happened" when a correction is doing the work.
 * @returns the applied offset in metres.
 */
export const resolveAppliedOffset = (projectGeoidOffset: number, userElevationOffset: number): number => {
  const geoid = Number.isFinite(projectGeoidOffset) ? projectGeoidOffset : 0;
  const user = Number.isFinite(userElevationOffset) ? userElevationOffset : 0;
  return geoid + user;
};

/**
 * The shift to apply to the ground (see OffsetElevationLayer). Lowering the terrain by the applied
 * offset lifts every native-Z data layer by the same amount at once.
 * @returns the ground offset in metres.
 */
export const resolveGroundOffset = (projectGeoidOffset: number, userElevationOffset: number): number =>
  -resolveAppliedOffset(projectGeoidOffset, userElevationOffset);

/**
 * What the elevation-offset input should show for a stored metres value in the given display
 * unit. Feet is a derived conversion of a metres-native value, so it rarely lands on a clean
 * decimal — rounded to 2dp for display. Metres is rounded too, but only to 6dp: far finer than
 * anyone would type by hand, so a genuinely-typed value is untouched, but it strips the floating-
 * point noise a value picks up from a metres->feet->metres round trip (e.g. a value entered as
 * feet and later viewed in metres) — left unrounded, that noise would display verbatim as
 * something like "4.998720000000001" instead of "4.99872".
 * Trailing zeros are dropped in both units, so 0 reads as "0" whichever unit is selected.
 * @returns the string to put in the input.
 */
export const formatOffsetDraft = (valueMetres: number, unit: DisplayUnit, feetPerMetre: number): string => {
  const displayValue = toDisplayValue(valueMetres, unit, feetPerMetre);
  if (unit === 'ft') return String(Number(displayValue.toFixed(2)));
  return String(Number(displayValue.toFixed(6)));
};

/**
 * Parses what the reader typed into the elevation-offset input, in the given display unit, back
 * to metres for storage. An empty field or a lone "-" (mid-typing a negative number) is treated as
 * an explicit 0, matching the field's own placeholder semantics rather than being rejected.
 * @returns the value in metres, or null when the typed text isn't a number at all.
 */
export const parseOffsetDraftToMetres = (draft: string, unit: DisplayUnit, feetPerMetre: number): number | null => {
  const trimmed = draft.trim();
  const displayValue = trimmed === '' || trimmed === '-' ? 0 : Number(trimmed);
  if (!Number.isFinite(displayValue)) return null;
  return fromDisplayValue(displayValue, unit, feetPerMetre);
};
